import { ApiFehler, NICHT_VORHANDEN, api, ohne404 } from '../api'
import { zeitpunkt } from '../format'
import { useAktion, useLaden } from '../hooks'
import { QUELLE } from '../texte'
import Meldung from './Meldung'
import type { JournalEintrag } from '../typen'

/**
 * Das Journal (18.09.2026): jede schreibende Aktion mit Zeit, Quelle, Titel, Weg (Methode und Pfad)
 * und dem Grund, warum sie eine Freigabe brauchte. Wo das Backend den Stand davor kennt, steht
 * „Rückgängig"; ein 409 heißt, der Stand ist inzwischen ein anderer - der Text kommt als Meldung.
 */
export default function Journal() {
  const { daten: geholt, fehler: ladeFehler, neuLaden } = useLaden<JournalEintrag[] | typeof NICHT_VORHANDEN>(() => ohne404(api.journal(50)), [])
  const { fehler, erfolg, setErfolg, arbeitet, ausfuehren } = useAktion()
  const fehlt = geholt === NICHT_VORHANDEN
  const daten = fehlt ? null : geholt

  function rueckgaengig(e: JournalEintrag) {
    if (!window.confirm(`„${e.titel}“ vom ${zeitpunkt(e.zeitpunkt)} rückgängig machen? Der Stand davor wird wiederhergestellt.`)) return
    void ausfuehren(async () => {
      try {
        await api.journalRueckgaengig(e.id)
      } catch (f) {
        // 409: der Stand ist inzwischen ein anderer - die Liste danach frisch holen, damit die Zeile
        // nicht weiter „Rückgängig" anbietet.
        if (f instanceof ApiFehler && f.status === 409) neuLaden()
        throw f
      }
      setErfolg(`„${e.titel}“ ist rückgängig gemacht.`)
      neuLaden()
    })
  }

  return (
    <div className="karte">
      <div className="leiste">
        <b>Journal</b>
        <span className="hinweis">Die letzten 50 schreibenden Aktionen - aus dem Gespräch, der Oberfläche und dem Postfach.</span>
      </div>
      {ladeFehler && <Meldung art="fehler">Das Journal ließ sich nicht laden: {ladeFehler}</Meldung>}
      {fehler && <Meldung art="fehler">{fehler}</Meldung>}
      {erfolg && <Meldung art="gut">{erfolg}</Meldung>}
      {fehlt && <p className="hinweis">Diese Auswertung liefert das Backend noch nicht.</p>}
      {!fehlt && (
        <div className="tabellenrahmen">
          <table className="journal-tabelle">
            <thead>
              <tr>
                <th>Zeit</th>
                <th>Quelle</th>
                <th>Aktion</th>
                <th>Freigabe</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {daten && daten.length === 0 && (
                <tr>
                  <td colSpan={5} className="leer">
                    Noch keine Aktion.
                  </td>
                </tr>
              )}
              {(daten ?? []).map((e) => (
                <tr key={e.id} className={e.rueckgaengig_am ? 'leise' : undefined}>
                  <td>{zeitpunkt(e.zeitpunkt)}</td>
                  <td>
                    {QUELLE[e.quelle] ?? e.quelle}
                    {e.unterhaltung_id !== null && <span className="hinweis"> (Gespräch {e.unterhaltung_id})</span>}
                  </td>
                  <td title={[`${e.methode} ${e.pfad}`, e.argumente ? JSON.stringify(e.argumente, null, 2) : ''].filter(Boolean).join('\n')}>
                    {e.titel}
                    {e.werkzeug && <div className="hinweis">{e.werkzeug}</div>}
                    {e.ergebnis_status !== null && e.ergebnis_status >= 400 && <div className="hinweis">fehlgeschlagen ({e.ergebnis_status})</div>}
                  </td>
                  <td>{e.freigabe ?? <span className="hinweis">keine nötig</span>}</td>
                  <td>
                    {e.rueckgaengig_am && <span className="hinweis">rückgängig am {zeitpunkt(e.rueckgaengig_am)}</span>}
                    {!e.rueckgaengig_am && e.rueckgaengig_moeglich && (
                      <button disabled={arbeitet} onClick={() => rueckgaengig(e)}>
                        Rückgängig
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
